import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import {
  useDeviceInstallations,
  type DeviceInstallationListParams,
} from "@/hooks/use-device-installations";
import {
  ComplianceFilters,
  OFFERED_VERDICTS,
  type ComplianceFilterValues,
} from "@/components/compliance/compliance-filters";
import {
  useComplianceFacilityColumns,
  useComplianceDeviceColumns,
} from "@/components/compliance/compliance-columns";
import { AccountLens } from "@/components/compliance/account-lens";
import { PremiseInspectionsLens } from "@/components/compliance/premise-inspections-lens";
import { AwaitingReviewStatCard } from "@/components/compliance/awaiting-review-stat-card";
import { ChangeRequestsStatCard } from "@/components/compliance/change-requests-stat-card";
import { PivotToggle, type CompliancePivot } from "@/components/compliance/pivot-toggle";
import { PageHeader, DataTable, Pagination, EmptyState, SkeletonTable } from "@/components/ui";

type DeviceInstallationListItem = components["schemas"]["DeviceInstallationListItem"];
type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];

interface DashboardSearch {
  account_id?: string;
  verdict?: ComplianceVerdict;
  pivot?: CompliancePivot;
  // URL-seeded drill axes (seed the API params directly; no UI control):
  // `enforced` narrows to devices at facilities under enforcement, `overdue`
  // pairs with `verdict=failed` to narrow to overdue-and-failed devices.
  enforced?: boolean;
  overdue?: boolean;
}

function parseBool(v: unknown): boolean | undefined {
  return v === true || v === "true" ? true : undefined;
}

// The deep-link allow-list is the filter bar's own option list — one source, so
// a link can never carry a verdict the select cannot show. `compliant` is
// excluded there: the projection only surfaces non-compliant rows, so
// `?verdict=compliant` would render this page empty, and a flag drill-down must
// never emit it.
function parseVerdict(v: unknown): ComplianceVerdict | undefined {
  return typeof v === "string" && (OFFERED_VERDICTS as readonly string[]).includes(v)
    ? (v as ComplianceVerdict)
    : undefined;
}

// Closed pivot deep-link allow-list — exactly the PivotToggle lens set. The
// three enforcement pivots share the device-installation projection; the
// premise_inspections lens reads the overdue premise-inspection assignment
// projection instead, so deep links (e.g. from an ops runbook) can land
// directly on it. Unknown values fall back to the facility default.
const PIVOTS: CompliancePivot[] = ["facility", "account", "device", "premise_inspections"];

function parsePivot(v: unknown): CompliancePivot | undefined {
  return typeof v === "string" && (PIVOTS as string[]).includes(v)
    ? (v as CompliancePivot)
    : undefined;
}

export const Route = createFileRoute("/_authenticated/backflow/out-of-compliance/")({
  component: ComplianceDashboardPage,
  validateSearch: (s: Record<string, unknown>): DashboardSearch => ({
    account_id: typeof s.account_id === "string" ? s.account_id : undefined,
    verdict: parseVerdict(s.verdict),
    pivot: parsePivot(s.pivot),
    enforced: parseBool(s.enforced),
    overdue: parseBool(s.overdue),
  }),
});

const PAGE_SIZE = 20;

type DeviceSortBy = NonNullable<DeviceInstallationListParams["sort_by"]>;

// Closed allow-list of sort_by keys the column factories may produce. Mirrors
// the `listDeviceInstallations` OpenAPI enum AND the Go ORDER BY allow-list.
// `facility_name` is absent BY DESIGN — the device list has no facility sort
// key, so both facility columns declare `enableSorting: false` rather than
// emitting a key this gate would swallow.
const SORT_KEYS = new Set<DeviceSortBy>([
  "verdict",
  "next_test_due_date",
  "device_status",
  "serial_number",
  "account",
]);

function ComplianceDashboardPage() {
  const { t } = useTranslation(["compliance", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const navigate = useNavigate();

  const search = Route.useSearch();
  const [pivot, setPivot] = useState<CompliancePivot>(search.pivot ?? "facility");
  const [filters, setFilters] = useState<ComplianceFilterValues>({
    account_id: search.account_id,
    verdict: search.verdict,
  });
  // Drill-only axes seeded once from the URL (no UI control — an external
  // deep link is the only source; the filter bar never mutates them).
  const [drill] = useState({
    enforced: search.enforced,
    overdue: search.overdue,
  });
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "next_test_due_date", desc: false }]);

  const firstSort = sorting[0];
  const sortBy =
    firstSort && SORT_KEYS.has(firstSort.id as DeviceSortBy)
      ? (firstSort.id as DeviceSortBy)
      : undefined;
  const sortDir: "asc" | "desc" | undefined = firstSort
    ? firstSort.desc
      ? "desc"
      : "asc"
    : undefined;

  useEffect(() => {
    setOffset(0);
  }, [sorting, filters, pivot]);

  const params = useMemo<DeviceInstallationListParams>(
    () => ({
      ...filters,
      ...drill,
      // The default scope is the ACTIVE non-compliant worklist per AWWA M14
      // §6.5. On this op the flag ANDs with `verdict` (it does not replace the
      // scope the way the retired matrix op did), so a selected verdict DROPS
      // it — `verdict=terminal` with the flag on would return zero rows and the
      // terminal drill would lie.
      non_compliant_only: filters.verdict === undefined ? true : undefined,
      sort_by: sortBy,
      sort_dir: sortDir,
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, drill, sortBy, sortDir, offset],
  );

  const { data, isLoading, isError } = useDeviceInstallations(params);
  const facilityColumns = useComplianceFacilityColumns();
  const deviceColumns = useComplianceDeviceColumns();

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = (row: DeviceInstallationListItem) => {
    void navigate({
      to: "/facilities/$facilityId",
      params: { facilityId: row.facility_id },
    });
  };

  const showTable = pivot === "facility" || pivot === "device";
  const columns = pivot === "device" ? deviceColumns : facilityColumns;

  return (
    <div className="p-8">
      <PageHeader title={t("title")} subtitle={t("subtitle", { count: total })} />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AwaitingReviewStatCard />
        <ChangeRequestsStatCard />
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <PivotToggle value={pivot} onChange={setPivot} />
          <p className="text-sm text-muted">{t(`pivotDescription.${pivot}`)}</p>
        </div>
        {/* The account + verdict axes only apply to the device projection —
            the premise-inspections lens reads the overdue assignment
            projection, which accepts neither, so the bar hides there instead
            of silently no-opping. */}
        {pivot !== "premise_inspections" && (
          <ComplianceFilters
            filters={filters}
            onChange={(f) => {
              setFilters(f);
              setOffset(0);
            }}
          />
        )}
      </div>

      {pivot === "premise_inspections" ? (
        <div className="mt-6 flex flex-col">
          <PremiseInspectionsLens />
        </div>
      ) : (
        <>
          {isError && (
            <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">{t("loadFailed")}</div>
          )}

          {isLoading ? (
            <div className="mt-6">
              <SkeletonTable columns={5} rows={5} />
            </div>
          ) : items.length === 0 ? (
            <div className="mt-6">
              <EmptyState title={t("empty")} description={t("emptyDescription")} />
            </div>
          ) : showTable ? (
            <>
              <div className="mt-6">
                <DataTable
                  columns={columns}
                  data={items}
                  onRowClick={handleRowClick}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
              />
            </>
          ) : (
            <>
              <div className="mt-6">
                <AccountLens items={items} />
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
